import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Loader2, CheckCircle, Edit3, Send, Sparkles,
  MessageSquare, FileText, Image, Clipboard, ArrowRight, Mail, Clock,
  Download, Paperclip, Eye
} from 'lucide-react'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

const STATUT_CONFIG = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  soumise: { label: 'Soumise par le client', color: 'bg-blue-100 text-blue-700' },
  convertie: { label: 'Convertie en sinistre', color: 'bg-emerald-100 text-emerald-700' },
}

function C1Avatar({ size = 24 }) {
  return (
    <div className="shrink-0 flex items-center justify-center rounded-full font-bold text-white"
      style={{ width: size, height: size, fontSize: size * 0.38, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}
    >C1</div>
  )
}

function formatSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' o'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' Ko'
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo'
}

function DocumentsPanel({ preDeclarationId }) {
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!preDeclarationId) return
    fetch(`${API}/api/pre-declarations/${preDeclarationId}/documents`)
      .then(r => r.json())
      .then(data => setDocs(data.documents || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [preDeclarationId])

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 justify-center text-slate-400">
        <Loader2 size={14} className="animate-spin" /> Chargement...
      </div>
    )
  }

  if (docs.length === 0) {
    return (
      <div className="text-center py-6">
        <Paperclip size={20} className="mx-auto mb-2 text-slate-300" />
        <p className="text-xs text-slate-400">Aucun document rattache</p>
      </div>
    )
  }

  return (
    <div className="space-y-2" data-testid="documents-list">
      {docs.map(doc => (
        <div key={doc.id} className="flex items-center gap-2.5 p-2.5 bg-slate-50 rounded-lg border border-slate-100" data-testid={`doc-${doc.id}`}>
          <div className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center bg-white border border-slate-200">
            {(doc.mime_type || '').startsWith('image/') ? (
              <Image size={14} className="text-blue-500" />
            ) : (
              <FileText size={14} className="text-red-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-700 truncate">{doc.nom_fichier}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-slate-400 uppercase">{doc.type_document}</span>
              {doc.taille && <span className="text-[10px] text-slate-400">{formatSize(doc.taille)}</span>}
            </div>
          </div>
          {doc.url && (
            <a href={doc.url} target="_blank" rel="noopener noreferrer"
              className="shrink-0 p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
              title="Ouvrir le document"
              data-testid={`doc-view-${doc.id}`}
            >
              <Eye size={14} />
            </a>
          )}
        </div>
      ))}
    </div>
  )
}

export default function PreDeclarationV4Detail({ tenants }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [pd, setPd] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [editedData, setEditedData] = useState({})
  const [converting, setConverting] = useState(false)
  const [draftLoading, setDraftLoading] = useState(false)
  const [declarationDraft, setDeclarationDraft] = useState(null)

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/pre-declarations/${id}`)
        if (!r.ok) throw new Error('Not found')
        const data = await r.json()
        setPd(data.pre_declaration)
        setEditedData(data.pre_declaration?.donnees_structurees || {})
      } catch {
        toast.error('Pre-declaration non trouvee')
        navigate('/c2/pre-declarations')
      } finally {
        setLoading(false)
      }
    })()
  }, [id, navigate])

  async function handleConvert() {
    setConverting(true)
    try {
      const res = await fetch(`${API}/api/pre-declarations/${id}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ donnees_structurees: editMode ? editedData : pd?.donnees_structurees }),
      })
      const data = await res.json()
      if (data.success && data.sinistre_id) {
        toast.success('Sinistre cree !')
        navigate(`/c2/sinistre/${data.sinistre_id}`)
      } else {
        toast.error(data.error || data.detail || 'Erreur conversion')
      }
    } catch { toast.error('Erreur conversion') }
    finally { setConverting(false) }
  }

  async function handleDraftDeclaration() {
    setDraftLoading(true)
    try {
      const res = await fetch(`${API}/api/pre-declarations/${id}/draft-declaration`, { method: 'POST' })
      const data = await res.json()
      if (data.declaration) {
        setDeclarationDraft(data.declaration)
        toast.success('Brouillon declaration genere')
      } else { toast.error('Erreur generation') }
    } catch { toast.error('Erreur generation') }
    finally { setDraftLoading(false) }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-indigo-500 mr-3" />
        <span className="text-sm text-slate-500">Chargement...</span>
      </div>
    )
  }

  if (!pd) return null

  const tenantName = (tenants || []).find(t => t.id === pd.tenant_id)?.nom || pd.tenants?.nom || pd.tenant_id
  const statusCfg = STATUT_CONFIG[pd.statut] || STATUT_CONFIG.brouillon
  const messages = pd.messages_chat || []
  const data = pd.donnees_structurees || {}
  const isSubmitted = pd.statut === 'soumise'
  const isConverted = pd.statut === 'convertie'

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="predecl-v4-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/c2/pre-declarations')} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid="back-btn">
          <ArrowLeft size={18} className="text-slate-500" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900">Pre-declaration — {tenantName}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusCfg.color}`}>
              {statusCfg.label}
            </span>
            <span className="text-xs text-slate-400">
              {pd.source === 'chat_client' ? 'Soumise par le client' : 'Creee par gestionnaire'}
            </span>
            <span className="text-xs text-slate-400">
              {new Date(pd.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>

      {/* Actions bar */}
      {(isSubmitted || (Object.keys(data).length > 0 && !isConverted)) && (
        <div className="bg-white border-2 border-blue-200 rounded-xl p-4 flex items-center gap-3" data-testid="actions-bar">
          <Sparkles size={18} className="text-blue-500 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-700">
              {isSubmitted ? 'Le client a soumis cette declaration — A traiter' : 'Pre-declaration prete'}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">Verifiez les donnees, modifiez si necessaire, puis convertissez en sinistre</p>
          </div>
          <button
            onClick={handleConvert}
            disabled={converting}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 shadow-sm"
            data-testid="convert-btn"
          >
            {converting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            Convertir en sinistre
          </button>
          <button
            onClick={handleDraftDeclaration}
            disabled={draftLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 shadow-sm"
            data-testid="draft-btn"
          >
            {draftLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Declaration assureur
          </button>
        </div>
      )}

      {isConverted && pd.sinistre_id && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="converted-banner">
          <CheckCircle size={18} className="text-emerald-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-700">Convertie en sinistre</p>
          </div>
          <button
            onClick={() => navigate(`/c2/sinistre/${pd.sinistre_id}`)}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
          >
            <ArrowRight size={14} /> Voir le sinistre
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Structured data */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="structured-data">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700">Donnees structurees</h2>
            {!isConverted && (
              <button onClick={() => setEditMode(!editMode)} className="flex items-center gap-1 text-xs text-indigo-600 font-medium" data-testid="edit-data-btn">
                <Edit3 size={12} /> {editMode ? 'Apercu' : 'Modifier'}
              </button>
            )}
          </div>

          {Object.keys(data).length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">Aucune donnee structuree extraite</p>
          ) : editMode ? (
            <div className="space-y-2.5">
              {Object.entries(editedData).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <label className="text-xs text-slate-500 w-36 shrink-0 text-right capitalize">{key.replace(/_/g, ' ')}:</label>
                  <input
                    value={value || ''}
                    onChange={e => setEditedData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="flex-1 text-xs border border-slate-200 rounded px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(data).map(([key, value]) => (
                value && (
                  <div key={key} className="flex gap-2">
                    <span className="text-xs text-slate-400 w-36 shrink-0 text-right capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="text-xs text-slate-700 font-medium">{String(value)}</span>
                  </div>
                )
              ))}
            </div>
          )}

          {pd.analyse_ia?.police_identifiee && (
            <div className="mt-3 px-2.5 py-1.5 bg-emerald-50 rounded-md text-xs text-emerald-700">
              Police identifiee: {pd.analyse_ia.police_identifiee}
            </div>
          )}
        </div>

        {/* Documents panel */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="documents-panel">
          <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Paperclip size={14} /> Pieces jointes
          </h2>
          <DocumentsPanel preDeclarationId={id} />
        </div>

        {/* Chat history */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="chat-history">
          <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <MessageSquare size={14} /> Conversation ({messages.length})
          </h2>
          {messages.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">Pas de conversation enregistree</p>
          ) : (
            <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-1.5`}>
                  {msg.role === 'assistant' && <C1Avatar size={20} />}
                  <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                    {msg.files && msg.files.length > 0 && (
                      <div className="mt-1 space-y-0.5">
                        {(Array.isArray(msg.files) ? msg.files : []).map((f, j) => (
                          <div key={j} className="flex items-center gap-1 text-xs opacity-75">
                            <FileText size={9} /> {typeof f === 'string' ? f : f.name || f}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Draft declaration */}
      {declarationDraft && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="declaration-draft">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Brouillon declaration assureur</h2>
            <button
              onClick={() => { navigator.clipboard.writeText(declarationDraft); toast.success('Copie !') }}
              className="flex items-center gap-1 text-xs text-indigo-600 font-medium"
              data-testid="copy-draft-btn"
            >
              <Clipboard size={12} /> Copier
            </button>
          </div>
          <pre className="text-xs text-slate-600 whitespace-pre-wrap bg-slate-50 p-4 rounded-md leading-relaxed">{declarationDraft}</pre>
        </div>
      )}
    </div>
  )
}
